import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Megaphone, Loader2, Plus, Paperclip, Trash2, Users, UserCheck, Clock, Download, X, AlertTriangle, Pin, PinOff, Eye, ChevronDown, ChevronUp, MessageCircle,
} from "lucide-react";

const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const kb = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export const BuildingNotices = ({ building, residents = [] }) => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDel, setToDel] = useState(null);
  const [readOpen, setReadOpen] = useState({});

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [selIds, setSelIds] = useState([]);
  const [expiresAt, setExpiresAt] = useState("");
  const [publishAt, setPublishAt] = useState("");
  const [pinned, setPinned] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [files, setFiles] = useState([]);

  const active = building.subscription_status === "attivo";

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/buildings/${building.id}/notices`); setNotices(r.data || []); }
    catch { /* silenzioso */ }
    finally { setLoading(false); }
  }, [building.id]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setTitle(""); setBody(""); setAudience("all"); setSelIds([]); setExpiresAt(""); setPublishAt(""); setPinned(false); setFiles([]);
  };

  const openNew = () => {
    if (!active) return toast.error("Condominio non collegato: attivalo in 'Acquisti e collegamento' per pubblicare avvisi.");
    resetForm(); setOpen(true);
  };

  const toggleRecipient = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelIds((s) => (s.length === residents.length ? [] : residents.map((r) => r.id)));

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setFiles((f) => [...f, ...list]);
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, k) => k !== i));

  const save = async () => {
    if (!title.trim() && !body.trim()) return toast.error("Inserisci un titolo o un testo.");
    if (audience === "selected" && selIds.length === 0) return toast.error("Seleziona almeno un destinatario.");
    setSaving(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("body", body.trim());
    fd.append("audience", audience);
    fd.append("recipient_ids", JSON.stringify(audience === "selected" ? selIds : []));
    if (expiresAt) fd.append("expires_at", new Date(expiresAt).toISOString());
    if (publishAt) fd.append("publish_at", new Date(publishAt).toISOString());
    fd.append("pinned", pinned ? "true" : "false");
    fd.append("send_sms", sendSms ? "true" : "false");
    fd.append("origin_url", window.location.origin);
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/buildings/${building.id}/notices`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(publishAt && new Date(publishAt) > new Date() ? "Avviso programmato: comparirà alla data scelta" : "Avviso pubblicato e notificato via email ai destinatari");
      setOpen(false); resetForm();
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Pubblicazione non riuscita"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!toDel) return;
    try {
      await api.delete(`/buildings/${building.id}/notices/${toDel.id}`);
      setNotices((p) => p.filter((x) => x.id !== toDel.id));
      toast.success("Avviso eliminato");
      setToDel(null);
    } catch { toast.error("Errore eliminazione"); }
  };

  const togglePin = async (n) => {
    try {
      await api.post(`/buildings/${building.id}/notices/${n.id}/pin`, { pinned: !n.pinned });
      toast.success(n.pinned ? "Avviso rimosso dall'evidenza" : "Avviso messo in evidenza");
      await load();
    } catch { toast.error("Operazione non riuscita"); }
  };

  const downloadAtt = async (notice, att) => {
    try {
      const r = await api.get(`/building-notices/${notice.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Download non riuscito"); }
  };

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden h-full flex flex-col" data-testid="building-notices">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-head text-lg font-semibold flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#007BFF]" />Bacheca avvisi — {building.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Pubblica avvisi con allegati per tutti o per condòmini scelti. Compaiono nella Home del portale e scadono in automatico.</p>
        </div>
        <Button data-testid="notice-new-button" onClick={openNew} disabled={!active}
          className="bg-[#007BFF] hover:bg-[#0056B3] h-9" title={!active ? "Condominio non collegato" : ""}>
          <Plus className="h-4 w-4 mr-1.5" />Nuovo avviso
        </Button>
      </div>

      {loading ? (
        <div className="p-8 flex flex-1 justify-center items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : notices.length === 0 ? (
        <div className="p-8 flex-1 flex items-center justify-center text-center text-muted-foreground text-sm">Nessun avviso pubblicato. Usa "Nuovo avviso" per comunicare con i condòmini.</div>
      ) : (
        <div className="divide-y divide-border flex-1">
          {notices.map((n) => (
            <div key={n.id} data-testid={`notice-row-${n.id}`} className={`px-6 py-3.5 ${n.is_expired ? "opacity-60" : ""} ${n.pinned ? "bg-red-50/60 border-l-2 border-red-500" : ""}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {n.pinned && <span data-testid={`notice-pinned-${n.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-red-600 text-white rounded-full px-2 py-0.5"><Pin className="h-3 w-3" />In evidenza</span>}
                    {n.title && <span className="text-sm font-semibold">{n.title}</span>}
                    {n.audience === "all" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-secondary rounded-full px-2 py-0.5 text-muted-foreground"><Users className="h-3 w-3" />Tutti</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#007BFF]/10 text-[#007BFF] rounded-full px-2 py-0.5"><UserCheck className="h-3 w-3" />{(n.recipient_names || []).length || (n.recipient_ids || []).length} destinatari</span>
                    )}
                    {n.is_expired && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5"><AlertTriangle className="h-3 w-3" />Scaduto</span>}
                    {n.scheduled && <span data-testid={`notice-scheduled-${n.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5"><Clock className="h-3 w-3" />Programmato</span>}
                  </div>
                  {n.body && <div className="text-sm text-foreground/80 mt-1 whitespace-pre-wrap">{n.body}</div>}
                  {(n.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {n.attachments.map((a) => (
                        <button key={a.id} data-testid={`notice-att-${a.id}`} onClick={() => downloadAtt(n, a)}
                          className="inline-flex items-center gap-1.5 text-[11px] bg-secondary hover:bg-[#007BFF]/10 hover:text-[#007BFF] border border-border rounded-md px-2 py-1 transition-colors">
                          <Paperclip className="h-3 w-3" />{a.filename}<span className="text-muted-foreground">· {kb(a.size)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-1.5 flex-wrap">
                    <span>{fmtDateTime(n.created_at)}</span>
                    {n.scheduled && n.publish_at && <><span>·</span><span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3 w-3" />Pubblica il {fmtDateTime(n.publish_at)}</span></>}
                    {n.expires_at && <><span>·</span><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Scade {fmtDateTime(n.expires_at)}</span></>}
                    {!n.expires_at && <><span>·</span><span>Senza scadenza</span></>}
                    <span>·</span>
                    {(n.reads_count || 0) > 0 ? (
                      <button data-testid={`notice-reads-toggle-${n.id}`} onClick={() => setReadOpen((s) => ({ ...s, [n.id]: !s[n.id] }))} className="inline-flex items-center gap-1 text-emerald-600 hover:underline">
                        {readOpen[n.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}<Eye className="h-3 w-3" />Letto da {n.reads_count}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />Non ancora letto</span>
                    )}
                  </div>
                  {readOpen[n.id] && (n.readers || []).length > 0 && (
                    <div className="mt-2 border border-border rounded-md divide-y divide-border" data-testid={`notice-readers-${n.id}`}>
                      {n.readers.map((rd, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                          <span className="font-medium inline-flex items-center gap-1.5"><Eye className="h-3 w-3 text-emerald-600" />{rd.name}</span>
                          <span className="text-muted-foreground">{fmtDateTime(rd.read_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button data-testid={`notice-pin-${n.id}`} onClick={() => togglePin(n)} title={n.pinned ? "Togli dall'evidenza" : "Metti in evidenza"} className={`p-1.5 rounded-md hover:bg-secondary ${n.pinned ? "text-red-600" : "text-muted-foreground hover:text-red-600"}`}>{n.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}</button>
                  <button data-testid={`notice-delete-${n.id}`} onClick={() => setToDel(n)} title="Elimina avviso" className="p-1.5 rounded-md hover:bg-secondary hover:text-red-600 text-muted-foreground"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog nuovo avviso */}
      <Dialog open={open} onOpenChange={(o) => !o && !saving && setOpen(false)}>
        <DialogContent data-testid="notice-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#007BFF]" />Nuovo avviso</DialogTitle>
            <DialogDescription>{building.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Titolo</label>
              <Input data-testid="notice-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Interruzione acqua giovedì" className="h-9 mt-1" autoFocus />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Testo</label>
              <Textarea data-testid="notice-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Scrivi il contenuto dell'avviso…" className="mt-1 text-sm resize-none" />
            </div>
            <label data-testid="notice-pinned-toggle" className="flex items-center gap-2.5 cursor-pointer rounded-md border border-border px-3 py-2.5 hover:bg-secondary/50">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="accent-red-600 h-4 w-4" />
              <Pin className={`h-4 w-4 ${pinned ? "text-red-600" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">Metti in evidenza</div>
                <div className="text-[11px] text-muted-foreground">L'avviso viene fissato in cima al portale con badge rosso "In evidenza".</div>
              </div>
            </label>
            <label data-testid="notice-sms-toggle" className="flex items-center gap-2.5 cursor-pointer rounded-md border border-border px-3 py-2.5 hover:bg-secondary/50">
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="accent-emerald-600 h-4 w-4" data-testid="notice-sms-checkbox" />
              <MessageCircle className={`h-4 w-4 ${sendSms ? "text-emerald-600" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">Avvisa via SMS</div>
                <div className="text-[11px] text-muted-foreground">Invia un SMS ai condòmini con il link al portale (usa la quota SMS del condominio · piani Premium/Elite).</div>
              </div>
            </label>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Destinatari</label>
              <div className="flex gap-2 mt-1">
                <button data-testid="notice-audience-all" onClick={() => setAudience("all")} type="button"
                  className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-sm font-medium border transition-colors ${audience === "all" ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border hover:bg-secondary"}`}>
                  <Users className="h-4 w-4" />Tutti i condòmini
                </button>
                <button data-testid="notice-audience-selected" onClick={() => setAudience("selected")} type="button"
                  className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-sm font-medium border transition-colors ${audience === "selected" ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border hover:bg-secondary"}`}>
                  <UserCheck className="h-4 w-4" />Solo scelti
                </button>
              </div>
              {audience === "selected" && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] text-muted-foreground">{selIds.length}/{residents.length} selezionati</span>
                    {residents.length > 0 && (
                      <button data-testid="notice-toggle-all" type="button" onClick={toggleAll} className="text-[11px] text-[#007BFF] hover:underline">
                        {selIds.length === residents.length ? "Deseleziona tutti" : "Seleziona tutti"}
                      </button>
                    )}
                  </div>
                  {residents.length === 0 ? (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Nessun condòmino in anagrafica.</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="notice-recipients">
                      {residents.map((r) => (
                        <label key={r.id} data-testid={`notice-recipient-${r.id}`} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-secondary/50 text-sm">
                          <input type="checkbox" checked={selIds.includes(r.id)} onChange={() => toggleRecipient(r.id)} className="accent-[#007BFF]" />
                          <span className="font-medium">{r.nome}</span>
                          {r.ruolo && <span className="text-[10px] uppercase text-muted-foreground">· {r.ruolo}</span>}
                          {r.email && <span className="text-xs text-muted-foreground ml-auto truncate">{r.email}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Programma pubblicazione — facoltativa</label>
              <Input data-testid="notice-publish-at" type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} className="h-9 mt-1" />
              <p className="text-[11px] text-muted-foreground mt-1">Se imposti una data futura, l'avviso comparirà (e la notifica email partirà) automaticamente a quel momento. Lascia vuoto per pubblicare subito.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Scadenza (data e ora) — facoltativa</label>
              <Input data-testid="notice-expires" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="h-9 mt-1" />
              <p className="text-[11px] text-muted-foreground mt-1">Alla scadenza l'avviso viene rimosso automaticamente dal portale di tutti. Lascia vuoto per nessuna scadenza.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Allegati — facoltativi</label>
              <label className="mt-1 flex items-center gap-2 cursor-pointer text-sm text-[#007BFF] hover:underline w-fit">
                <input data-testid="notice-files" type="file" multiple className="hidden" onChange={addFiles} />
                <Paperclip className="h-4 w-4" />Aggiungi file
              </label>
              {files.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="notice-files-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-secondary rounded-md px-2.5 py-1.5">
                      <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{f.name}</span>
                      <span className="text-muted-foreground">{kb(f.size)}</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annulla</Button>
            <Button data-testid="notice-publish" onClick={save} disabled={saving} className="bg-[#007BFF] hover:bg-[#0056B3]">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Pubblicazione…</> : <><Megaphone className="h-4 w-4 mr-2" />Pubblica avviso</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDel} onOpenChange={(o) => !o && setToDel(null)}>
        <AlertDialogContent data-testid="delete-notice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'avviso?</AlertDialogTitle>
            <AlertDialogDescription>L'avviso <b>{toDel?.title || "(senza titolo)"}</b> sarà rimosso dal portale dei condòmini.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-notice" onClick={(e) => { e.preventDefault(); del(); }} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
